import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, AuthShell, BrandMark, Button, TextField } from '@argink/ui';
import { useAuth } from '../auth/AuthContext';
import {
  clearPendingOwnerOtp,
  isValidOwnerOtpHandle,
  persistPendingOwnerOtp,
  readPendingOwnerOtp,
} from '../auth/pendingOwnerOtp';
import { ApiError } from '../lib/api/types';

type Step = 'email' | 'code';

/**
 * As falhas que o backend REALMENTE emite (`apps/api/src/routes/web-auth.ts`).
 * Nenhuma a mais: o §26 do mandato proíbe estado visual que o servidor não saiba
 * distinguir, e cada variante abaixo corresponde a uma resposta distinta.
 *
 * Ausência notável — **não existe estado "código expirado"**. O backend funde
 * inválido, expirado e já utilizado num único `422 invalid_otp`, de propósito:
 * separá-los diria a um atacante que o código que ele chutou existiu. A tela
 * repete a fusão em vez de adivinhar qual dos três aconteceu.
 */
type AuthFault =
  | { kind: 'invalid' }
  | { kind: 'exhausted' }
  | { kind: 'rate_limited'; retryAfterSeconds: number | null }
  | { kind: 'unavailable' };

/**
 * MESMA falha, consequências diferentes. `rate_limited` conta *solicitações* de
 * código no pedido e *tentativas* de acerto na verificação — são dois
 * contadores distintos em `owner-auth/rate-limit.ts`, e chamar os dois de
 * "tentativas" descreveria errado o que o usuário precisa parar de fazer.
 *
 * `resend` é a fase que carrega o invariante mais delicado da tela: um reenvio
 * que falha **não invalida o código já enviado**. A mensagem tem de dizer isso,
 * senão o usuário abandona um código perfeitamente utilizável.
 */
type FaultPhase = 'request' | 'resend' | 'verify';

interface FaultState {
  fault: AuthFault;
  phase: FaultPhase;
}

/** Traduz a exceção do client no fato que o servidor afirmou — nunca num palpite. */
function faultFrom(error: unknown): AuthFault {
  if (!(error instanceof ApiError)) return { kind: 'unavailable' };
  if (error.status === 429) {
    if (error.code === 'otp_attempts_exhausted') return { kind: 'exhausted' };
    return { kind: 'rate_limited', retryAfterSeconds: error.retryAfterSeconds };
  }
  if (error.status === 422 && error.code === 'invalid_otp') return { kind: 'invalid' };
  return { kind: 'unavailable' };
}

const FAULT_TITLE: Record<AuthFault['kind'], string> = {
  invalid: 'Não foi possível confirmar',
  exhausted: 'Este código foi bloqueado',
  rate_limited: 'Aguarde para tentar de novo',
  unavailable: 'Não foi possível continuar',
};

/**
 * Contagem regressiva ancorada no `retry-after` do servidor.
 *
 * O parâmetro é o número que o backend mandou; o hook só decrementa o relógio
 * local a partir dele. É a diferença entre "o servidor disse 47s" e um timer
 * inventado no cliente — o segundo é proibido pelo §21 e mentiria toda vez que
 * o limite fosse compartilhado entre abas, dispositivos ou instâncias.
 */
function useServerCooldown(seconds: number | null): number {
  const [remaining, setRemaining] = useState(seconds ?? 0);

  useEffect(() => {
    setRemaining(seconds ?? 0);
  }, [seconds]);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setTimeout(() => setRemaining((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  return remaining;
}

/** `/login` — e-mail + OTP do owner (AMVP-03/04), redesenhado em M3 sobre o
 * `AuthShell`. Sem signup e sem senha: o backend autentica por código de uso
 * único, então não existe "esqueci minha senha" a construir (D-075). E-mail não
 * provisionado recebe a MESMA resposta de sucesso (anti-enumeração, IMVP-08). O
 * código nunca aparece aqui: ele viaja por e-mail (Mailpit em dev). */
export function LoginPage() {
  const { isAuthenticated, isLoading, requestOtp, verifyOtp } = useAuth();
  const location = useLocation() as { state?: { from?: string } };
  const [pendingOtp] = useState(readPendingOwnerOtp);
  const [step, setStep] = useState<Step>(() => (pendingOtp ? 'code' : 'email'));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verificationHandle, setVerificationHandle] = useState<string | null>(
    pendingOtp?.verificationHandle ?? null,
  );
  const [faultState, setFaultState] = useState<FaultState | null>(null);
  const fault = faultState?.fault ?? null;
  const [submitting, setSubmitting] = useState(false);
  /* Incrementa a cada código emitido — inclusive num reenvio, quando `step` já é
   * 'code' e portanto não muda. É o que permite devolver o foco ao campo sem
   * depender de remontagem. */
  const [codeIssued, setCodeIssued] = useState(0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const cooldownSeconds =
    fault?.kind === 'rate_limited' ? (fault.retryAfterSeconds ?? null) : null;
  const remainingCooldown = useServerCooldown(cooldownSeconds);

  /**
   * O desafio morreu no servidor: o único caminho é pedir outro código.
   *
   * Derivado da AUSÊNCIA DO HANDLE, não da falha corrente. Ler `fault === 'exhausted'`
   * era um bug: bastava a falha seguinte ser outra — pedir código novo dentro do
   * cooldown de 60 s devolve `rate_limited` — para o campo e o "Confirmar"
   * ressuscitarem sobre um handle já descartado. Clicar não fazia nada, em
   * silêncio, porque `handleVerifyOtp` retorna cedo sem handle.
   */
  const challengeIsDead = step === 'code' && verificationHandle === null;

  /* Foco explícito em vez de `autoFocus`: `autoFocus` só age na montagem, e no
   * reenvio o input já está na tela. Um efeito ancorado em `codeIssued` cobre os
   * dois casos com um mecanismo só — e é determinístico, ao contrário de agendar
   * o foco num quadro de animação. */
  useEffect(() => {
    if (step === 'code' && !challengeIsDead) codeInputRef.current?.focus();
  }, [step, codeIssued, challengeIsDead]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to={location.state?.from ?? '/dashboard'} replace />;
  }

  async function requestCode(event: FormEvent | undefined, isResend: boolean) {
    event?.preventDefault();
    if (isResend && !email) return;

    const phase: FaultPhase = isResend ? 'resend' : 'request';
    setFaultState(null);
    setSubmitting(true);
    try {
      const result = await requestOtp(email);
      if (!isValidOwnerOtpHandle(result.verificationHandle)) {
        // Uma resposta 2xx malformada não pode trocar o desafio ainda utilizável.
        setFaultState({ fault: { kind: 'unavailable' }, phase });
        return;
      }
      setVerificationHandle(result.verificationHandle);
      persistPendingOwnerOtp(result.verificationHandle);
      setStep('code');
      setCode('');
      setCodeIssued((n) => n + 1);
    } catch (error) {
      // No reenvio, mantém o desafio anterior: uma falha de rede, de limite ou de
      // origem não prova que o código já enviado tenha morrido. Por isso nada é
      // limpo aqui — nem `code`, nem `verificationHandle`, nem o storage.
      setFaultState({ fault: faultFrom(error), phase });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRequestOtp(event: FormEvent) {
    await requestCode(event, false);
  }

  async function handleResendOtp() {
    await requestCode(undefined, true);
  }

  async function handleVerifyOtp(event: FormEvent) {
    event.preventDefault();
    if (!verificationHandle) return;
    setFaultState(null);
    setSubmitting(true);
    try {
      await verifyOtp({ verificationHandle, code });
      clearPendingOwnerOtp();
    } catch (error) {
      const observed = faultFrom(error);
      setFaultState({ fault: observed, phase: 'verify' });
      if (observed.kind === 'exhausted') {
        // O handle não vale mais nada; guardá-lo faria a próxima visita abrir
        // direto num desafio morto.
        clearPendingOwnerOtp();
        setVerificationHandle(null);
        setCode('');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function backToEmail() {
    setStep('email');
    setCode('');
    setFaultState(null);
  }

  /** Colar "123 456" ou "código: 123456" deve funcionar: o usuário cola o que o
   * e-mail lhe deu, não o que o `pattern` espera. */
  function handleCodePaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text');
    const digits = pasted.replace(/\D/g, '').slice(0, 6);
    if (!digits) return;
    event.preventDefault();
    setCode(digits);
  }

  const faultMessage = faultState
    ? renderFaultMessage(faultState, remainingCooldown, challengeIsDead)
    : null;

  /**
   * Só o cooldown do contador de PEDIDO bloqueia o reenvio.
   *
   * `rate-limit.ts` tem dois contadores independentes, e o `retry-after` de cada
   * um vale para a ação que ele conta: estourar tentativas de verificação devolve
   * `retry-after: 600` (a janela do desafio), enquanto o servidor emitiria um
   * código novo em 60 s. Usar aquele número para desabilitar o reenvio travava a
   * única saída por 10 minutos — um estado que o backend não afirma, exatamente o
   * que o §26 proíbe.
   */
  const resendCooldown =
    fault?.kind === 'rate_limited' && faultState?.phase !== 'verify' ? remainingCooldown : 0;
  const resendBlocked = submitting || resendCooldown > 0;

  return (
    <AuthShell
      brand={<BrandMark variant="wordmark" width={148} />}
      title={step === 'email' ? 'Entrar' : 'Confirme o código'}
      description={
        step === 'email'
          ? 'Informe seu e-mail para receber um código de acesso.'
          : 'Digite o código de 6 dígitos enviado para o seu e-mail.'
      }
      footer={
        step === 'code' ? (
          <Button type="button" variant="outline" onClick={backToEmail}>
            Usar outro e-mail
          </Button>
        ) : null
      }
      aside={
        <div className="login-aside">
          <p className="login-aside__lead">A confiança deixa rastro.</p>
          <p className="login-aside__body">
            Cada assinatura feita no ARGINK guarda quem assinou, quando e por qual método — uma
            trilha que continua disponível depois que o processo termina.
          </p>
        </div>
      }
    >
      {step === 'email' ? (
        <form onSubmit={handleRequestOtp} aria-label="Entrar com e-mail" className="login-form">
          <TextField
            label="E-mail"
            type="email"
            name="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {faultMessage}
          <Button type="submit" fullWidth isLoading={submitting}>
            Enviar código
          </Button>
          {verificationHandle ? (
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={() => {
                setFaultState(null);
                setStep('code');
              }}
            >
              Já tenho um código
            </Button>
          ) : null}
        </form>
      ) : (
        <form
          onSubmit={handleVerifyOtp}
          aria-label="Confirmar código recebido por e-mail"
          className="login-form"
        >
          {challengeIsDead ? null : (
            <TextField
              ref={codeInputRef}
              label="Código"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={code}
              onPaste={handleCodePaste}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
          )}
          {faultMessage}
          {/* O submit não fica `disabled` até completar 6 dígitos: botão morto não
            * explica o que falta. O `required` + `pattern` do campo barram o envio
            * curto e dizem o motivo, sem gastar tentativa no servidor. */}
          {challengeIsDead ? null : (
            <Button type="submit" fullWidth isLoading={submitting}>
              Confirmar
            </Button>
          )}
          {/* Sem e-mail em memória não há para onde reenviar — é o caso do desafio
            * restaurado de `sessionStorage` depois de um reload, que guarda o
            * handle opaco e nunca o e-mail. Antes, o botão simplesmente sumia e o
            * usuário com o desafio bloqueado ficava sem saída nenhuma na tela.
            * Agora ele volta ao passo de e-mail, que é onde o código novo nasce. */}
          {!email && challengeIsDead ? (
            <Button type="button" variant="primary" fullWidth onClick={backToEmail}>
              Receber um novo código
            </Button>
          ) : null}
          {email ? (
            <Button
              type="button"
              variant={challengeIsDead ? 'primary' : 'outline'}
              fullWidth
              isLoading={submitting}
              disabled={resendBlocked}
              onClick={handleResendOtp}
            >
              {challengeIsDead ? 'Receber um novo código' : 'Reenviar código'}
            </Button>
          ) : null}
        </form>
      )}
    </AuthShell>
  );
}

/**
 * Uma frase por causa real, sem vocabulário de API. O `role="alert"` do `Alert`
 * é o que faz o leitor de tela anunciar a mudança sem mover o foco do campo.
 */
function renderFaultMessage(
  { fault, phase }: FaultState,
  remainingCooldown: number,
  challengeIsDead: boolean,
) {
  const isResend = phase === 'resend';
  return (
    <Alert
      tone={fault.kind === 'rate_limited' ? 'warning' : 'danger'}
      title={isResend ? 'Não foi possível reenviar' : FAULT_TITLE[fault.kind]}
    >
      {faultSentence(fault, phase, remainingCooldown)}
      {/* O invariante da tela: reenvio que falha não mata o código já enviado.
        * Sem esta frase o usuário desiste de um código que ainda funciona.
        *
        * Mas ela só é VERDADE enquanto existe um código atual. Depois de
        * `otp_attempts_exhausted` o desafio foi bloqueado, e repetir a frase aqui
        * mandaria o usuário insistir num código morto — a mentira exatamente
        * oposta à que a frase existe para evitar. */}
      {isResend && !challengeIsDead ? ' O código atual continua válido.' : null}
    </Alert>
  );
}

function faultSentence(fault: AuthFault, phase: FaultPhase, remainingCooldown: number): string {
  switch (fault.kind) {
    case 'invalid':
      return 'Código inválido, expirado ou já utilizado.';
    case 'exhausted':
      return 'Houve tentativas demais com este código. Peça um novo para continuar.';
    case 'rate_limited': {
      // Na verificação o servidor está contando ERROS de código; no pedido, está
      // contando ENVIOS. Dizer "tentativas" nos dois casos descreveria errado o
      // que o usuário precisa parar de fazer para sair do limite.
      const what = phase === 'verify' ? 'Muitas tentativas' : 'Muitas solicitações';
      return remainingCooldown > 0
        ? `${what} em pouco tempo. Tente de novo em ${formatCooldown(remainingCooldown)}.`
        : `${what} em pouco tempo. Já é possível tentar de novo.`;
    }
    case 'unavailable':
      return 'Não foi possível concluir agora. Tente novamente em instantes.';
  }
}

/** `90` vira "1 min 30 s", `45` vira "45 s". Segundos crus acima de um minuto
 * obrigam o usuário a fazer a conta. */
function formatCooldown(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
}
